<template>
  <!-- Every cell stays on one line, so virtual rows keep one height; clipped text shows on hover -->
  <TruncatedTextReveal
    class="production-devices-table-container position-relative"
    :class="{ 'production-devices-table-container--compact': compactIdentity }"
  >
    <DefineScheduledTransactionsIcon v-slot="{ device }">
      <font-awesome-icon
        v-if="showScheduledTransactions && device.has_scheduled_transactions"
        icon="fa-solid fa-circle-info"
        class="text-info flex-shrink-0"
        :title="
          t(
            'dashboard.consolidated.production_devices_table.scheduled_transactions_tooltip'
          )
        "
      />
    </DefineScheduledTransactionsIcon>
    <DataTable
      ref="devicesTable"
      :value="flatDevices"
      v-model:filters="filters"
      dataKey="id"
      filterDisplay="row"
      :globalFilterFields="[
        'company_name',
        'device_name',
        'device_eic',
        'technology',
      ]"
      class="production-devices-datatable border rounded"
      v-bind="virtualScrollProps"
      @filter="resetScroll"
      @sort="resetScroll"
      sortField="company_name"
      :sortOrder="1"
      :rowClass="rowClass"
      :pt="{ bodyRow: bodyRowPassThrough }"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">
          {{ t("dashboard.consolidated.production_devices_table.no_devices") }}
        </div>
      </template>

      <!-- Selection -->
      <Column
        v-if="selectable"
        headerStyle="width: 2.5rem"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #header>
          <input
            type="checkbox"
            class="form-check-input mt-0"
            :checked="headerSelectionState === 'checked'"
            :indeterminate="headerSelectionState === 'partial'"
            :disabled="pickableShownIds.length === 0"
            :aria-label="t('device_groups.select_all_shown')"
            @change="toggleAllShown"
          />
        </template>
        <template #body="{ data }">
          <input
            type="checkbox"
            class="form-check-input mt-0"
            :checked="selectedIdSet.has(data.id)"
            :disabled="data.locked_by !== null"
            :aria-label="
              data.locked_by ??
              t('device_groups.select_device', { device: data.device_eic })
            "
            @change="toggleDevice(data.id)"
          />
        </template>
      </Column>

      <!-- Company Name -->
      <Column
        v-if="showCompanyColumn"
        field="company_name"
        :header="
          t('dashboard.consolidated.production_devices_table.company_name')
        "
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 10rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="text-truncate">{{ data.company_name }}</div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueCompanies"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            filter
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else class="text-muted">{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Technology -->
      <Column
        field="technology"
        :header="
          t('dashboard.consolidated.production_devices_table.technology')
        "
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 6.5rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="d-flex align-items-center">
            <TechnologyIcon
              :technology="data.technology"
              class="me-2 flex-shrink-0"
            />
            <span class="text-truncate">{{
              translateTechnology(data.technology, t)
            }}</span>
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueTechnologies"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Device: the ID, then its name when the registry gives one that differs -->
      <Column
        v-if="compactIdentity"
        field="device_eic"
        filterField="device_search"
        :header="t('device_groups.device_column')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: hasDistinctNames ? '16rem' : '11.5rem' }"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="d-flex align-items-center gap-2">
            <span
              class="text-truncate flex-shrink-0 font-variant-numeric-tabular"
            >
              {{ data.device_eic }}
            </span>
            <span
              v-if="data.device_name && data.device_name !== data.device_eic"
              class="text-truncate text-body-tertiary"
            >
              {{ data.device_name }}
            </span>
            <ReuseScheduledTransactionsIcon :device="data" />
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.filter')"
          />
        </template>
      </Column>

      <!-- Device EIC -->
      <Column
        v-if="!compactIdentity"
        field="device_eic"
        :header="t('dashboard.consolidated.production_devices_table.device_id')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 9rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.filter')"
          />
        </template>
      </Column>

      <!-- Device Name -->
      <Column
        v-if="!compactIdentity"
        field="device_name"
        :header="
          t('dashboard.consolidated.production_devices_table.device_name')
        "
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 12rem"
      >
        <template #body="{ data }">
          <div class="d-flex align-items-center gap-1">
            <span class="text-truncate">{{ data.device_name }}</span>
            <ReuseScheduledTransactionsIcon :device="data" />
          </div>
        </template>
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.filter')"
          />
        </template>
      </Column>

      <!-- Device group -->
      <Column
        v-if="showGroupColumn"
        field="device_group_name"
        :header="t('device_groups.group_column')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="text-truncate">{{ data.device_group_name ?? "-" }}</div>
        </template>
      </Column>

      <!-- Address -->
      <Column
        field="address"
        :header="t('registry_device.address')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="compactIdentity ? undefined : { width: '9rem' }"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="text-truncate">{{ data.address ?? "-" }}</div>
        </template>
      </Column>

      <!-- Capacity (kW) -->
      <Column
        field="device_power"
        :header="`${t('dashboard.consolidated.production_devices_table.capacity')} (${t('common.kw')})`"
        sortable
        dataType="numeric"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 5.5rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="text-end fw-semibold font-variant-numeric-tabular">
            {{ data.device_power?.toLocaleString() }}
          </div>
        </template>
      </Column>

      <!-- COD -->
      <Column
        field="commercial_operation_date"
        :header="t('registry_device.commercial_operation_date')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 6.5rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          {{
            data.commercial_operation_date
              ? formatDateTimeIntoDate(data.commercial_operation_date)
              : "-"
          }}
        </template>
      </Column>

      <!-- Supported -->
      <Column
        field="support"
        :header="t('registry_device.supported_device')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 7.5rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span
            class="badge rounded-pill badge-compact"
            :class="
              data.support
                ? 'bg-warning-subtle text-warning-emphasis'
                : 'bg-success-subtle text-success-emphasis'
            "
          >
            {{ data.support ? t("common.yes") : t("common.no") }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="supportOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Ecolabel -->
      <Column
        field="ecolabel_approved"
        :header="t('registry_device.ecolabel_approved')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 7.25rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span v-if="data.ecolabel_approved !== null">
            <span
              class="badge rounded-pill badge-compact"
              :class="
                data.ecolabel_approved
                  ? 'bg-success-subtle text-success-emphasis'
                  : 'bg-secondary-subtle text-secondary-emphasis'
              "
            >
              {{ data.ecolabel_approved ? t("common.yes") : t("common.no") }}
            </span>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="ecolabelOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- LT Reporting Status (Lithuanian companies only) -->
      <Column
        v-if="showLtReportingStatus"
        field="lt_reporting_status"
        :header="t('dashboard.automaticReporting.title')"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 8rem"
      >
        <template #body="{ data }">
          <span
            v-if="data.lt_reporting_status"
            class="badge rounded-pill badge-compact"
            :class="getLtStatusBadgeClass(data.lt_reporting_status)"
          >
            {{ getLtStatusLabel(data.lt_reporting_status) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
      </Column>

      <!-- Actions -->
      <Column
        :headerStyle="{ width: compactIdentity ? '2.75rem' : '3.5rem' }"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #body="{ data }">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary btn-icon"
            :title="
              t(
                'dashboard.consolidated.production_devices_table.view_projection'
              )
            "
            :aria-label="
              t(
                'dashboard.consolidated.production_devices_table.view_projection'
              )
            "
            @click="openProjectionModal(data)"
          >
            <font-awesome-icon icon="chart-column" />
          </button>
        </template>
      </Column>
    </DataTable>

    <ScrollMoreIndicator
      :scrollTarget="scrollElement"
      :itemHeight="ROW_HEIGHT_PX"
    >
      <template #default="{ count }">
        ~{{
          t("dashboard.consolidated.production_devices_table.more_devices", {
            count,
          })
        }}
      </template>
    </ScrollMoreIndicator>

    <ProductionEstimateOverrideModal
      ref="projectionModalRef"
      :companyId="activeCompanyId"
    />
  </TruncatedTextReveal>
</template>

<script setup lang="ts">
import ScrollMoreIndicator from "@/components/common/ScrollMoreIndicator.vue";
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import TruncatedTextReveal from "@/components/common/TruncatedTextReveal.vue";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import {
  useVirtualScrolledTable,
  type VirtualScrolledTable,
} from "@/composables/useVirtualScrolledTable";
import ProductionEstimateOverrideModal from "@/dashboard/production_strategy/ProductionEstimateOverrideModal.vue";
import { Company, RegistryDevice } from "@/types";
import { groupSelectionState, toggleGroup } from "@/utils/groupSelection";
import { FilterMatchMode } from "@primevue/core/api";
import { createReusableTemplate } from "@vueuse/core";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { getSortIcon } from "@/utils/sortIcons";
import { translateTechnology } from "@/utils/technologies";

export type LtReportingStatusMap = Map<
  number,
  "missing_association" | "declared" | "pending_approval" | "rejected"
>;

const props = withDefaults(
  defineProps<{
    companies: (Company & { id: number })[];
    registryDevices: RegistryDevice[];
    companyId?: number;
    ltReportingStatuses?: LtReportingStatusMap;
    // Show an info icon next to devices that have registry scheduled transactions configured.
    showScheduledTransactions?: boolean;
    /** Adds a checkbox column whose picks are the `selection` model. */
    selectable?: boolean;
    showCompanyColumn?: boolean;
    /** Adds a column naming each device's group (`device_group_name`). */
    showGroupColumn?: boolean;
    /**
     * Why a device can't be selected, shown as its row's title, or null when it can. A locked row is
     * greyed out and its checkbox disabled. Only consulted while `selectable`.
     */
    lockedBy?: (device: RegistryDevice) => string | null;
    /**
     * Merges Device ID and Name into one Device column (the name shown only where it differs from
     * the ID), lets Address (and Group) take the width the fixed columns leave, and scrolls sideways
     * below 57rem rather than squeezing them.
     */
    compactIdentity?: boolean;
  }>(),
  {
    companyId: undefined,
    ltReportingStatuses: undefined,
    showScheduledTransactions: false,
    selectable: false,
    showCompanyColumn: true,
    showGroupColumn: false,
    lockedBy: undefined,
    compactIdentity: false,
  }
);

const selection = defineModel<number[]>("selection", { default: () => [] });

// PERF: must match the rendered row height or the virtual scroller's offsets drift. 0.375rem cell
// padding top and bottom + the 28px chart button + the 1px row border = 41px.
const ROW_HEIGHT_PX = 41;

const { t } = useI18n();

// PrimeVue exposes `processedData` (the rows its filters and sort leave) at runtime but not in its types.
type DevicesDataTable = VirtualScrolledTable & { processedData?: FlatDevice[] };
const devicesTable = useTemplateRef<DevicesDataTable>("devicesTable");
const {
  tableProps: virtualScrollProps,
  resetScroll,
  scrollElement,
} = useVirtualScrolledTable(
  devicesTable,
  () => flatDevices.value.length,
  ROW_HEIGHT_PX
);

const projectionModalRef = ref<InstanceType<
  typeof ProductionEstimateOverrideModal
> | null>(null);

const activeCompanyId = ref(props.companyId ?? props.companies[0]?.id ?? 0);

const openProjectionModal = async (device: FlatDevice) => {
  activeCompanyId.value = props.companyId ?? device.company_id;
  await nextTick();
  projectionModalRef.value?.show(device as unknown as RegistryDevice);
};

const showLtReportingStatus = computed(
  () => props.ltReportingStatuses != null && props.ltReportingStatuses.size > 0
);

const filters = ref({
  company_name: { value: [], matchMode: FilterMatchMode.IN },
  technology: { value: [], matchMode: FilterMatchMode.IN },
  device_eic: { value: null, matchMode: FilterMatchMode.CONTAINS },
  device_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  device_search: { value: null, matchMode: FilterMatchMode.CONTAINS },
  support: { value: [], matchMode: FilterMatchMode.IN },
  ecolabel_approved: { value: [], matchMode: FilterMatchMode.IN },
});

export interface FlatDevice extends Omit<RegistryDevice, "support"> {
  company_name: string;
  /** The ID and name together, for the compact Device column's filter. */
  device_search: string;
  support: boolean;
  lt_reporting_status: string | null;
  /** From `lockedBy`: why the row can't be selected, or null. */
  locked_by: string | null;
}

const [DefineScheduledTransactionsIcon, ReuseScheduledTransactionsIcon] =
  createReusableTemplate<{ device: FlatDevice }>();

const flatDevices = computed<FlatDevice[]>(() => {
  const companyMap = new Map(props.companies.map((c) => [c.id, c.name ?? ""]));
  return props.registryDevices.map((device) => ({
    ...device,
    company_name: companyMap.get(device.company_id) ?? "",
    device_search: `${device.device_eic} ${device.device_name ?? ""}`,
    support:
      (device.support as unknown) === true ||
      String(device.support).toLowerCase() === "true",
    lt_reporting_status: props.ltReportingStatuses?.get(device.id) ?? null,
    locked_by: props.selectable ? (props.lockedBy?.(device) ?? null) : null,
  }));
});

// Most registries name a device by its ID; only when some name differs does the Device column need
// room to show it beside the ID.
const hasDistinctNames = computed(() =>
  flatDevices.value.some(
    (device) => device.device_name && device.device_name !== device.device_eic
  )
);

const uniqueCompanies = computed(() =>
  [...new Set(flatDevices.value.map((d) => d.company_name))]
    .filter(Boolean)
    .sort()
    .map((name) => ({ label: name, value: name }))
);

const uniqueTechnologies = computed(() =>
  [...new Set(flatDevices.value.map((d) => d.technology))]
    .filter(Boolean)
    .sort()
    .map((tech) => ({
      label: translateTechnology(tech, t),
      value: tech,
    }))
);

const supportOptions = computed(() => [
  { label: t("common.yes"), value: true },
  { label: t("common.no"), value: false },
]);

const ecolabelOptions = computed(() => [
  { label: t("common.yes"), value: true },
  { label: t("common.no"), value: false },
]);

const selectedIdSet = computed(() => new Set(selection.value));

// `row-disabled` is the global greyed-out row PortfolioItemsTable uses for rows it can't select.
const rowClass = (device: FlatDevice): string | undefined => {
  if (device.locked_by !== null) return "row-disabled";
  return selectedIdSet.value.has(device.id)
    ? "production-devices-table__row--selected"
    : undefined;
};

const bodyRowPassThrough = (options: { props: { rowData?: FlatDevice } }) => {
  const device = options.props.rowData;
  return device ? { title: device.locked_by ?? undefined } : {};
};

// The rows the filters leave, read from the DataTable so "select all shown" agrees with what it
// renders. `processedData` is a computed on the instance, so reading it through the ref tracks it.
const shownDevices = computed<FlatDevice[]>(
  () => devicesTable.value?.processedData ?? flatDevices.value
);
const shownIds = computed(() => shownDevices.value.map((device) => device.id));
const pickableShownIds = computed(() =>
  shownDevices.value
    .filter((device) => device.locked_by === null)
    .map((device) => device.id)
);

const headerSelectionState = computed(() =>
  groupSelectionState(shownIds.value, pickableShownIds.value, selection.value)
);

const toggleAllShown = () => {
  selection.value = toggleGroup(
    shownIds.value,
    pickableShownIds.value,
    selection.value
  );
};

const toggleDevice = (deviceId: number) => {
  selection.value = selectedIdSet.value.has(deviceId)
    ? selection.value.filter((id) => id !== deviceId)
    : [...selection.value, deviceId];
};

const getLtStatusBadgeClass = (status: string) => {
  if (status === "declared") return "bg-success-subtle text-success-emphasis";
  if (status === "pending_approval")
    return "bg-warning-subtle text-warning-emphasis";
  if (status === "rejected") return "bg-danger-subtle text-danger-emphasis";
  return "bg-warning-subtle text-warning-emphasis";
};

const getLtStatusLabel = (status: string) => {
  if (status === "declared")
    return t("dashboard.automaticReporting.automaticallyDeclared");
  if (status === "pending_approval")
    return t("dashboard.automaticReporting.pendingApproval");
  if (status === "rejected") return t("common.rejected");
  return t("dashboard.automaticReporting.missingAssociation");
};
</script>

<style scoped lang="scss">
// Scroll sideways rather than clip trailing columns when the fixed widths outgrow the card.
.production-devices-table-container {
  overflow-x: auto;
}

// In the compact layout the Group and Address columns share what the fixed columns leave. Below this
// width they would be unreadable, so the table scrolls sideways instead.
.production-devices-table-container--compact :deep(.pv-datatable-table) {
  min-width: 57rem;
}

// Header titles wrap between words only; the global break-word split "Supported" into "Support/ed".
// Each fixed column fits its longest header word in every locale plus the sort icon (Swedish
// "Subventionerad" and Latvian "Ekomarķējums" set the two widest).
:deep([data-pc-section="columntitle"]) {
  overflow-wrap: normal;
}

.production-devices-datatable :deep(.pv-datatable-body-row) {
  height: v-bind("`${ROW_HEIGHT_PX}px`");
}

:deep(.production-devices-table__row--selected) > td {
  background: var(--phoenix-primary-bg-subtle);
}

.badge-compact {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
}

.font-variant-numeric-tabular {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.btn-icon {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
